import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import retreatService from '../services/retreatService';

const RetreatRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const retreatId = searchParams.get('retreatId');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    dateOfBirth: '',
    gender: '',
    dietaryRestrictions: '',
    specialRequests: '',
    sessionId: '',
    accommodationType: 'shared',
    paymentMethod: 'credit_card',
    discountPercent: 0,
  });

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (retreatId) {
      fetchSessions();
    }
  }, [retreatId]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const data = await retreatService.getRetreatSessions(retreatId);
      setSessions(data || []);
    } catch (err) {
      setError('Failed to load sessions.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    try {
      setLoading(true);
      const registrationData = {
        retreatId: parseInt(retreatId),
        ...formData,
      };
      await retreatService.registerForRetreat(registrationData);
      setSuccess(true);
      setTimeout(() => {
        navigate('/retreat/center');
      }, 2000);
    } catch (err) {
      setError('Failed to complete registration. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container-custom max-w-4xl">
        <div className="mb-8">
          <button
            onClick={() => navigate('/retreat/selection')}
            className="text-blue-600 hover:text-blue-800 mb-4 font-semibold"
          >
            ← Back to Selection
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Retreat Registration</h1>
          <p className="text-lg text-gray-600">
            Complete the form below to register for the retreat
          </p>
        </div>

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800">
              Registration successful! Redirecting...
            </p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="card">
          {/* Personal Information */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-6">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Phone Number *</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Date of Birth *</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Gender *</label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Address *</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">State *</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
              <div>
                <label className="form-label">Zip Code *</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
            </div>
          </div>

          {/* Special Requirements */}
          <div className="mb-8 border-t pt-8">
            <h2 className="text-2xl font-bold mb-6">Special Requirements</h2>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="form-label">Dietary Restrictions</label>
                <textarea
                  name="dietaryRestrictions"
                  value={formData.dietaryRestrictions}
                  onChange={handleInputChange}
                  className="form-input"
                  rows="3"
                  placeholder="Please describe any dietary restrictions"
                />
              </div>
              <div>
                <label className="form-label">Special Requests</label>
                <textarea
                  name="specialRequests"
                  value={formData.specialRequests}
                  onChange={handleInputChange}
                  className="form-input"
                  rows="3"
                  placeholder="Any special requests or accommodations"
                />
              </div>
            </div>
          </div>

          {/* Session & Accommodation */}
          {sessions.length > 0 && (
            <div className="mb-8 border-t pt-8">
              <h2 className="text-2xl font-bold mb-6">Session & Accommodation</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="form-label">Session *</label>
                  <select
                    name="sessionId"
                    value={formData.sessionId}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  >
                    <option value="">Select a Session</option>
                    {sessions.map((session) => (
                      <option key={session.sessionId} value={session.sessionId}>
                        {session.sessionName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Accommodation Type *</label>
                  <select
                    name="accommodationType"
                    value={formData.accommodationType}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  >
                    <option value="shared">Shared</option>
                    <option value="private">Private</option>
                    <option value="dormitory">Dormitory</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Payment Information */}
          <div className="mb-8 border-t pt-8">
            <h2 className="text-2xl font-bold mb-6">Payment Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Payment Method *</label>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                >
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>
              <div>
                <label className="form-label">Discount Percent</label>
                <input
                  type="number"
                  name="discountPercent"
                  value={formData.discountPercent}
                  onChange={handleInputChange}
                  className="form-input"
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="border-t pt-8">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Complete Registration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RetreatRegistration;
